// Copyright (c) 2015-present Mattermost, Inc. All Rights Reserved.
// See LICENSE.txt for license information.

import React, {type ReactNode, type MouseEventHandler} from 'react';

import {FormattedMessage} from 'react-intl';
import {type OverlayTriggerProps} from 'react-bootstrap';

import classNames from 'classnames';

import OverlayTrigger from 'components/overlay_trigger';
import Tooltip from 'components/tooltip';
import UnreadBelowIcon from 'components/widgets/icons/unread_below_icon';
import CloseIcon from 'components/widgets/icons/close_icon';
import Constants from 'utils/constants';

import './toast.scss';

export type Props = {
    onClick?: MouseEventHandler<HTMLDivElement>;
    onClickMessage?: string;
    onDismiss?: () => void;
    children?: ReactNode;
    show: boolean;
    showActions?: boolean; //used for showing jump actions
    width: number;
    extraClasses?: string;
    overlayPlacement?: OverlayTriggerProps['placement'];
    shrink?: boolean;
}

export default class Toast extends React.PureComponent<Props> {
    static defaultProps = {
        overlayPlacement: 'bottom',
    }

    handleDismiss = () => {
        if (typeof this.props.onDismiss == 'function') {
            this.props.onDismiss();
        }
    }

    render() {
        const {show, extraClasses, showActions, width, overlayPlacement} = this.props;

        const jumpSection = () => {
            return (
                <div
                    className='toast__jump'
                >
                    <UnreadBelowIcon/>
                    {width > Constants.MOBILE_SCREEN_WIDTH && this.props.onClickMessage}
                </div>
            );
        };

        let closeTooltip = (<div/>);
        if (showActions && show) {
            closeTooltip = (
                <Tooltip id='toast-close__tooltip'>
                    <FormattedMessage
                        id='general_button.close'
                        defaultMessage='Close'
                    />
                    <div className='tooltip__shortcut--txt'>
                        <FormattedMessage
                            id='general_button.esc'
                            defaultMessage='esc'
                        />
                    </div>
                </Tooltip>
            );
        }

        const toastClass = classNames('toast', extraClasses, {toast__visible: show});
        const toastActionClass = classNames('toast__message', {
            toast__pointer: showActions,
            toast__shrink: this.props.shrink,
        });

        return (
            <div className={toastClass}>
                <div
                    className={toastActionClass}
                    onClick={showActions ? this.props.onClick : undefined}
                >
                    {showActions && jumpSection()}
                    {this.props.children}
                </div>
                <OverlayTrigger
                    delayShow={Constants.OVERLAY_TIME_DELAY}
                    placement={overlayPlacement}
                    overlay={closeTooltip}
                >
                    <div
                        className='toast__dismiss'
                        onClick={this.handleDismiss}
                        data-testid={extraClasses ? `dismissToast-${extraClasses}` : 'dismissToast'}
                    >
                        <CloseIcon
                            className='close-btn'
                            id='dismissToast'
                        />
                    </div>
                </OverlayTrigger>
            </div>
        );
    }
}
